/**
 * Credit Worker Service (credit-worker-business)
 * API Gateway path: /credit-worker/v2/backoffice
 * Collection, transfers, portfolio delinquency/defaulted
 */

import { apiGet, apiPost } from '@/lib/axios';
import type { DelinquencyPortfolioResponse, DefaultedPortfolioResponse } from '@/types/portfolio.types';
import type {
  DailyCollectionResponse,
  DelinquencyAnalysisResponse,
  TransferPending,
  TransferDetail,
  CollectionPaymentsResponse,
  CollectionAttemptsTodayResponse,
  ScheduledCouponsResponse,
} from '@/types/collection.types';

// RBAC v2: /credit-worker/v2/backoffice (W4 + @RequirePermission). Todos los endpoints
// de este service tienen gemelo /v2 verificado en el openapi de credit-worker.
const BASE = '/credit-worker/v2/backoffice';

// ─── Collection ──────────────────────────────────────────────────────────────

export const getDailyCollection = (date?: string) =>
  apiGet<DailyCollectionResponse>(`${BASE}/collection/daily`, { params: date ? { date } : undefined });

export const getDelinquencyAnalysis = () =>
  apiGet<DelinquencyAnalysisResponse>(`${BASE}/collection/delinquency`);

export const getCollectionPayments = (partnerId: string, from: string, to: string) =>
  apiGet<CollectionPaymentsResponse>(`${BASE}/collection/payments`, { params: { partnerId, from, to } });

export const getCollectionAttemptsToday = (partnerId: string) =>
  apiGet<CollectionAttemptsTodayResponse>(`${BASE}/collection/attempts/today`, { params: { partnerId } });

export const getScheduledCoupons = (partnerId: string, from?: string, to?: string) => {
  const params: Record<string, string> = { partnerId };
  if (from) params.from = from;
  if (to) params.to = to;
  return apiGet<ScheduledCouponsResponse>(`${BASE}/collection/scheduled`, { params });
};

// ─── Transfers ───────────────────────────────────────────────────────────────

export const getPendingTransfers = (partnerId: string) =>
  apiGet<TransferPending[]>(`${BASE}/transfers/pending`, { params: { partnerId } });

export const approveTransfer = (transferId: string, partnerId: string, bankReference: string) =>
  apiPost<void>(`${BASE}/transfers/${transferId}/approve`, { bankReference }, { params: { partnerId } });

export const getTransferHistory = (partnerId: string) =>
  apiGet<TransferPending[]>(`${BASE}/transfers`, { params: { partnerId } });

export const getTransferDetail = (transferId: string, partnerId: string) =>
  apiGet<TransferDetail>(`${BASE}/transfers/${transferId}`, { params: { partnerId } });

// ─── Portfolio ───────────────────────────────────────────────────────────────

export const getDelinquentPortfolio = () =>
  apiGet<DelinquencyPortfolioResponse>(`${BASE}/portfolio/delinquency`);

export const getDefaultedCredits = () =>
  apiGet<DefaultedPortfolioResponse>(`${BASE}/portfolio/defaulted`);

// ─── Credit Collection Records ──────────────────────────────────────────────

export const getCreditCollectionRecords = (
  creditId: string,
  opts?: { pendingOnly?: boolean; transferId?: string },
) => {
  // Tres modos excluyentes del listado: default | pendingOnly | transferId.
  // transferId gana si se envian ambos (mismo criterio que el backend).
  const params = opts?.transferId
    ? { transferId: opts.transferId }
    : opts?.pendingOnly
      ? { pendingOnly: 'true' }
      : undefined;
  return apiGet<any[]>(`${BASE}/credits/${creditId}/collection-records`, { params });
};

// ─── Dashboard V2: Collection ───────────────────────────────────────────────

export const getCollectionRecent = (days = 5) =>
  apiGet<any[]>(`${BASE}/collection/recent`, { params: { days } });

export const getCollectionWeekly = (weeks = 8) =>
  apiGet<any[]>(`${BASE}/collection/weekly`, { params: { weeks } });

export const getDelinquencyMonthly = (months = 6) =>
  apiGet<any[]>(`${BASE}/collection/delinquency/monthly`, { params: { months } });

export const getDelinquencyByLevel = () =>
  apiGet<any[]>(`${BASE}/collection/delinquency/by-level`);

export const getDelinquencyFlow = (weeks = 4) =>
  apiGet<any[]>(`${BASE}/collection/delinquency/flow`, { params: { weeks } });

export const getDelinquencyHistory = (months = 6) =>
  apiGet<any[]>(`${BASE}/collection/delinquency/history`, { params: { months } });

export const getCartera = (partnerId: string) =>
  apiGet<any[]>(`${BASE}/cartera`, { params: { partnerId } });

// ─── Transfers V2 (bilateral) ───────────────────────────────────────────────

/** Cobros acumulados pendientes de transferir a UNE-Credito */
export const getPendingCollections = () =>
  apiGet<any>(`${BASE}/transfers/pending-collections`);

/**
 * Crear transferencia agrupando cobros pendientes. Atomico:
 * el batch se guarda directo en status SENT con la referencia SPEI capturada.
 * `bankReference` es obligatorio — el backend rechaza con 400 MISSING_BANK_REFERENCE
 * si falta.
 */
export const createTransfer = (partnerId: string, bankReference: string, sentBy?: string) =>
  apiPost<any>(`${BASE}/transfers/create`, { partnerId, bankReference, sentBy });

/**
 * @deprecated Desde el fix de atomicidad (F1) el batch se crea directamente
 * en status SENT via createTransfer. Este endpoint sigue exportado por si
 * hay callers externos.
 */
export const registerTransferSent = (transferId: string, data: { partnerId: string; bankReference: string; sentBy: string }) =>
  apiPost<any>(`${BASE}/transfers/${transferId}/sent`, data);

/** UNE-Credito confirma recepción */
export const confirmTransferReceipt = (transferId: string, data: { partnerId: string; confirmedBy: string }) =>
  apiPost<any>(`${BASE}/transfers/${transferId}/confirm`, data);

/** Transferencias enviadas pendientes de confirmar por UNE-Credito */
export const getTransfersSent = (partnerId: string) =>
  apiGet<any[]>(`${BASE}/transfers/sent`, { params: { partnerId } });

// ─── WalletLien (cobro latente DEFAULTED) ────────────────────────────────────
//
// Spec: docs/1_modelo-credito/PROCESO_05_COBRANZA.md §9
// Endpoints backend en credit-worker BackofficeController.

import type {
  CreditLienResponse,
  LienDetailResponse,
  LiensListFilters,
  LiensListResponse,
} from '@/types/lien.types';

/** Lien activo + historial paginado de cobros para un credit DEFAULTED. */
export const getCreditLien = (creditId: string, pageSize = 20, lastKey?: any) =>
  apiGet<CreditLienResponse>(`${BASE}/credits/${creditId}/lien`, {
    params: {
      pageSize,
      ...(lastKey ? { lastKey: JSON.stringify(lastKey) } : {}),
    },
  });

/** Listado paginado de wallet liens con filtros opcionales. */
export const getLiensList = (filters: LiensListFilters = {}) =>
  apiGet<LiensListResponse>(`${BASE}/liens`, {
    params: {
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
      pageSize: filters.pageSize ?? 20,
      ...(filters.lastKey ? { lastKey: JSON.stringify(filters.lastKey) } : {}),
    },
  });

/** Detalle de un wallet lien + historial paginado. */
export const getLienDetail = (lienId: string, pageSize = 20, lastKey?: any) =>
  apiGet<LienDetailResponse>(`${BASE}/liens/${lienId}`, {
    params: {
      pageSize,
      ...(lastKey ? { lastKey: JSON.stringify(lastKey) } : {}),
    },
  });

// ─── Cierre Mensual (Fase B) ────────────────────────────────────────────────
import type {
  AccountingMonthlyFsaResponse,
  AccountingMonthlyUneResponse,
  AccountingMonthDetailFsaResponse,
  AccountingMonthDetailUneResponse,
  AccountingScope,
} from '@/types/accounting.types';

export const getAccountingMonthly = <T extends AccountingScope>(
  scope: T,
  partnerId: string,
  year: number,
) =>
  apiGet<T extends 'FSA' ? AccountingMonthlyFsaResponse : AccountingMonthlyUneResponse>(
    `${BASE}/accounting/monthly`,
    { params: { scope, partnerId, year } },
  );

export const getAccountingMonthDetail = <T extends AccountingScope>(
  scope: T,
  partnerId: string,
  yyyyMm: string,
) =>
  apiGet<T extends 'FSA' ? AccountingMonthDetailFsaResponse : AccountingMonthDetailUneResponse>(
    `${BASE}/accounting/monthly/${yyyyMm}`,
    { params: { scope, partnerId } },
  );
